import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "Obligaciones";

const ListarObligaciones = () => {
  const { id } = useParams();
  const [obligaciones, setObligaciones] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // ==============================
  // CONFIG TOKEN
  // ==============================
  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const normalizarTexto = (texto) =>
    String(texto || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");

  // ==============================
  // PERMISOS MÓDULO CONTRATOS
  // ==============================

  const getModuloObligaciones = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloObligaciones = getModuloObligaciones();

  const puedeCrear =
    moduloObligaciones.puedeCrear === true ||
    moduloObligaciones.crear === true;

  const puedeActualizar =
    moduloObligaciones.puedeActualizar === true ||
    moduloObligaciones.actualizar === true;

  const puedeEliminar =
    moduloObligaciones.puedeEliminar === true ||
    moduloObligaciones.eliminar === true;

  const deleteObservacion = useCallback(
    async (idObligacion) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar obligaciones.",
          "warning"
        );
        return;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "Esta obligación será eliminada permanentemente",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return;

        const { data } = await axios.delete(
          `/api/obligaciones/delete/${idObligacion}`,
          authConfig()
        );

        setObligaciones((prev) =>
          prev.filter((ob) => ob._id !== idObligacion)
        );

        Swal.fire({
          icon: "success",
          title: data.message || "Obligación eliminada correctamente",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error("Error al eliminar la obligación:", error);

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudo eliminar la obligación",
          "error"
        );

        getData();
      }
    },
    [puedeEliminar]
  );

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `/api/obligaciones/contrato/${id}`,
        authConfig()
      );

      if (response.data.ok) {
        const data = (response.data.obligaciones || []).map((item, index) => ({
          numero: index + 1,
          observacion: item.observacion,
          _id: item._id,

          funciones: (
            <div className="d-flex gap-2 justify-content-end align-items-center flex-nowrap">
              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{
                  width: 34,
                  height: 34,
                  opacity: puedeActualizar ? 1 : 0.5,
                  cursor: puedeActualizar ? "pointer" : "not-allowed",
                }}
                disabled={!puedeActualizar}
                title={
                  puedeActualizar
                    ? "Editar obligación"
                    : "Sin permiso para actualizar"
                }
                type="button"
                onClick={() => {
                  if (!puedeActualizar) {
                    Swal.fire(
                      "Sin permisos",
                      "No tiene permisos para actualizar obligaciones.",
                      "warning"
                    );
                    return;
                  }

                  navigate(`/instructor/obligaciones/editar/${item._id}`, {
                    state: { obligacion: item, contratoId: id },
                  });
                }}
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>

              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{
                  width: 34,
                  height: 34,
                  opacity: puedeEliminar ? 1 : 0.5,
                  cursor: puedeEliminar ? "pointer" : "not-allowed",
                }}
                disabled={!puedeEliminar}
                title={
                  puedeEliminar
                    ? "Eliminar obligación"
                    : "Sin permiso para eliminar"
                }
                type="button"
                onClick={() => deleteObservacion(item._id)}
              >
                <i className="fa-solid fa-trash"></i>
              </button>
            </div>
          ),
        }));

        setObligaciones(data);
      } else {
        setObligaciones([]);
      }
    } catch (error) {
      console.error("Error al cargar las obligaciones:", error);
      setObligaciones([]);
    } finally {
      setLoading(false);
    }
  }, [id, puedeActualizar, puedeEliminar, deleteObservacion, navigate]);

  useEffect(() => {
    if (id) getData();
  }, [id, getData]);

  const columns = useMemo(
    () => [
      { Header: "N°", accessor: "numero" },
      {
        Header: "Obligación",
        accessor: "observacion",
        width: 280,
        hideHeaderOnMobile: true,
      },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Obligaciones contractuales
                </h5>
                <small className="text-secondary">
                  Control y gestión de obligaciones asociadas al contrato.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate(-1)}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"
                      } me-2`}
                  ></i>
                  Actualizar
                </button>

                <button
                  className="btn btn-dark"
                  type="button"
                  disabled={!puedeCrear}
                  style={{
                    opacity: puedeCrear ? 1 : 0.5,
                    cursor: puedeCrear ? "pointer" : "not-allowed",
                  }}
                  onClick={() => {
                    if (!puedeCrear) {
                      Swal.fire(
                        "Sin permisos",
                        "No tiene permisos para crear obligaciones.",
                        "warning"
                      );
                      return;
                    }

                    navigate(`/instructor/obligaciones/nuevo/${id}`);
                  }}
                  title={
                    puedeCrear
                      ? "Nueva obligación"
                      : "Sin permiso para crear"
                  }
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Nuevo
                </button>
              </div>
            </div>
          </div>

          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>
                <div className="mt-3">Cargando obligaciones…</div>
              </div>
            ) : Array.isArray(obligaciones) && obligaciones.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={obligaciones} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">📑</div>
                <div className="fw-semibold">
                  No hay obligaciones registradas
                </div>
                <div className="small mb-3">
                  No se encontraron obligaciones para este contrato.
                </div>

                <button
                  className="btn btn-dark"
                  type="button"
                  disabled={!puedeCrear}
                  style={{
                    opacity: puedeCrear ? 1 : 0.5,
                    cursor: puedeCrear ? "pointer" : "not-allowed",
                  }}
                  onClick={() => {
                    if (!puedeCrear) {
                      Swal.fire(
                        "Sin permisos",
                        "No tiene permisos para crear obligaciones.",
                        "warning"
                      );
                      return;
                    }

                    navigate(`/instructor/obligaciones/nuevo/${id}`);
                  }}
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Crear obligación
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarObligaciones;